import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fetchClassificationAuditData } from "./classificationAuditExport";

interface DecisionProvenance {
  session_id: string;
  summary: string | null;
  decision_type: string | null;
  decision_subtype: string | null;
  phase: number;
  subphase: string;
  created_at: string;
  updated_at: string;
  created_by_name: string;
  created_by_email: string;
  classification_audit: any;
  commits: CommitRecord[];
  activity_trail: ActivityRecord[];
}

interface CommitRecord {
  id: string;
  committed_at: string;
  committed_by_name: string;
  commit_type: string;
  commit_hash: string;
  report_version: number;
  participants_snapshot: any;
  active_editors_at_commit: any;
}

interface ActivityRecord {
  timestamp: string;
  user_name: string;
  activity_type: string;
  entity_type: string;
  metadata: any;
}

/**
 * Generates a comprehensive decision provenance report for compliance.
 * Aggregates: sessions, classification audit, commits, and activity logs.
 */
export async function generateProvenanceReport(workspaceId: string) {
  // Fetch all data in parallel
  const [sessionsRes, commitsRes, activityRes, classificationAudit] = await Promise.all([
    supabase
      .from("decision_sessions")
      .select(`
        id, summary, decision_type, decision_subtype, phase, subphase,
        created_at, updated_at, meta,
        profiles:created_by ( name, email )
      `)
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .limit(500),
    supabase
      .from("decision_commits")
      .select(`
        id, committed_at, commit_type, commit_hash, report_version,
        participants_snapshot, active_editors_at_commit, session_id,
        profiles:committed_by ( name, email )
      `)
      .order("committed_at", { ascending: false })
      .limit(500),
    supabase
      .from("activity_logs")
      .select(`
        id, created_at, activity_type, entity_type, entity_id, metadata,
        profiles:user_id ( name, email )
      `)
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .limit(1000),
    fetchClassificationAuditData(workspaceId),
  ]);

  if (sessionsRes.error) throw sessionsRes.error;
  if (commitsRes.error) throw commitsRes.error;
  if (activityRes.error) throw activityRes.error;

  const sessions = sessionsRes.data || [];
  const commits = commitsRes.data || [];
  const activities = activityRes.data || [];

  // Index commits and activities by session_id
  const commitsBySession = new Map<string, any[]>();
  for (const c of commits) {
    const list = commitsBySession.get(c.session_id) || [];
    list.push(c);
    commitsBySession.set(c.session_id, list);
  }

  const activitiesBySession = new Map<string, any[]>();
  for (const a of activities) {
    if (a.entity_id) {
      const list = activitiesBySession.get(a.entity_id) || [];
      list.push(a);
      activitiesBySession.set(a.entity_id, list);
    }
  }

  // Index classification audit by session (from metadata if available)
  const classificationBySession = new Map<string, any[]>();
  for (const c of classificationAudit) {
    // Classification audit entries don't have session_id directly,
    // so they go in the global section
  }

  // Build provenance records
  const provenance: DecisionProvenance[] = sessions.map((s: any) => {
    const sessionCommits = (commitsBySession.get(s.id) || []).map((c: any) => ({
      id: c.id,
      committed_at: c.committed_at,
      committed_by_name: c.profiles?.name || "-",
      commit_type: c.commit_type,
      commit_hash: c.commit_hash,
      report_version: c.report_version,
      participants_snapshot: c.participants_snapshot,
      active_editors_at_commit: c.active_editors_at_commit,
    }));

    const sessionActivities = (activitiesBySession.get(s.id) || []).map((a: any) => ({
      timestamp: a.created_at,
      user_name: a.profiles?.name || "-",
      activity_type: a.activity_type,
      entity_type: a.entity_type,
      metadata: a.metadata,
    }));

    const classificationMeta = s.meta?.classification_audit || null;

    return {
      session_id: s.id,
      summary: s.summary,
      decision_type: s.decision_type,
      decision_subtype: s.decision_subtype,
      phase: s.phase,
      subphase: s.subphase,
      created_at: s.created_at,
      updated_at: s.updated_at,
      created_by_name: s.profiles?.name || "-",
      created_by_email: s.profiles?.email || "-",
      classification_audit: classificationMeta,
      commits: sessionCommits,
      activity_trail: sessionActivities,
    };
  });

  // Compute summary stats
  const totalDecisions = provenance.length;
  const withCommits = provenance.filter((p) => p.commits.length > 0).length;
  const totalCommits = commits.length;
  const overrideEntries = classificationAudit.filter((e) => e.was_override);
  const confirmedEntries = classificationAudit.filter((e) => !e.was_override);

  const reportDataWithoutHash = {
    report_type: "decision_provenance_compliance",
    report_version: "1.0",
    generated_at: new Date().toISOString(),
    workspace_id: workspaceId,
    summary: {
      total_decisions: totalDecisions,
      decisions_with_commits: withCommits,
      total_commits: totalCommits,
      total_activity_events: activities.length,
      classification_stats: {
        total: classificationAudit.length,
        confirmed: confirmedEntries.length,
        overridden: overrideEntries.length,
        override_rate_percent: classificationAudit.length
          ? Math.round((overrideEntries.length / classificationAudit.length) * 100)
          : 0,
      },
    },
    classification_audit: classificationAudit,
    decisions: provenance,
  };

  // Compute SHA-256 integrity hash over the report content
  const jsonString = JSON.stringify(reportDataWithoutHash);
  const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(jsonString));
  const integrityHash = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');

  const reportData = {
    ...reportDataWithoutHash,
    integrity_hash: integrityHash,
  };

  return reportData;
}

/**
 * Exports the full provenance report as a downloadable JSON file.
 */
export async function exportProvenanceJSON(workspaceId: string) {
  const report = await generateProvenanceReport(workspaceId);

  const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `decision-provenance-${format(new Date(), "yyyy-MM-dd")}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return report.summary;
}

/**
 * Exports provenance report as a CSV (flattened per decision).
 */
export async function exportProvenanceCSV(workspaceId: string) {
  const report = await generateProvenanceReport(workspaceId);

  const headers = [
    "Session-ID", "Zusammenfassung", "Entscheidungstyp", "Subtyp",
    "Phase", "Subphase", "Erstellt am", "Aktualisiert am",
    "Erstellt von", "Email",
    "Klassifizierung (System)", "Override",
    "Anzahl Commits", "Letzter Commit", "Letzter Commit-Hash",
    "Anzahl Aktivitäten",
  ];

  const rows = report.decisions.map((d: DecisionProvenance) => {
    const lastCommit = d.commits[0];
    const ca = d.classification_audit;
    return [
      d.session_id,
      d.summary || "-",
      d.decision_type || "-",
      d.decision_subtype || "-",
      String(d.phase),
      d.subphase,
      format(new Date(d.created_at), "dd.MM.yyyy HH:mm"),
      format(new Date(d.updated_at), "dd.MM.yyyy HH:mm"),
      d.created_by_name,
      d.created_by_email,
      ca ? `${ca.system_type}/${ca.system_subtype} (${ca.system_confidence}%)` : "-",
      ca ? (ca.was_override ? "Ja" : "Nein") : "-",
      String(d.commits.length),
      lastCommit ? format(new Date(lastCommit.committed_at), "dd.MM.yyyy HH:mm") : "-",
      lastCommit?.commit_hash || "-",
      String(d.activity_trail.length),
    ];
  });

  const csvContent = [
    headers.join(";"),
    ...rows.map((row: string[]) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")),
  ].join("\n");

  const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `decision-provenance-${format(new Date(), "yyyy-MM-dd")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return report.decisions.length;
}
